import { MutationFunction, MutationOptions, useMutation } from 'react-query';

import {
    Activity,
    ActivityResponse,
    createActivity,
    CreateActivityBody,
    DataItem,
    deleteActivity,
    DeleteActivityResponse,
    importActivitiesBySite,
    importLastNeeds,
    transferDailyElements,
    TransferDailyElementsBody,
    TransferDailyElementsResponse,
    updateActivitiesState,
    UpdateActivitiesStateBody,
    UpdateActivitiesStateQueries,
    updateActivity,
    updateActivityTruckManager,
    ValidateActivitiesBody,
    validateSiteActivities,
    ValidateSiteActivitiesResponse,
} from '../../services/apiSLTP/dailyReports/activities';
import {
    DuplicateActivitiesBody,
    duplicateActivitiesToDate,
    DuplicateActivityBody,
    duplicateActivityToDate,
} from '../../services/apiSLTP/dailyReports/planning';

type UseCreateActivityMutationOptions = MutationOptions<ActivityResponse, Error, CreateActivityBody>;

export const useCreateActivityMutation = (options?: UseCreateActivityMutationOptions) => {
    const activityMutation: MutationFunction<ActivityResponse, CreateActivityBody> = async (data) => {
        try {
            const response: ActivityResponse = await createActivity(data);
            return response;
        } catch (error) {
            throw new Error('An unexpected error occurred during activity');
        }
    };

    return useMutation(activityMutation, options);
};

type UseActivityTruckManagerMutationOptions = MutationOptions<ActivityResponse, Error, { activityId: string; isTruckManager: boolean }>;

export const useActivityTruckManagerMutation = (options?: UseActivityTruckManagerMutationOptions) => {
    const activityMutation: MutationFunction<ActivityResponse, { activityId: string; isTruckManager: boolean }> = async (data) => {
        try {
            const response: ActivityResponse = await updateActivityTruckManager(data.activityId, data.isTruckManager);
            return response;
        } catch (error) {
            throw new Error('An unexpected error occurred during activity');
        }
    };

    return useMutation(activityMutation, options);
};

type UseActivitiesStateMutationOptions = MutationOptions<
    ActivityResponse,
    Error,
    { body: UpdateActivitiesStateBody; queries?: UpdateActivitiesStateQueries }
>;

export const useActivitiesStateMutation = (options?: UseActivitiesStateMutationOptions) => {
    const activityMutation: MutationFunction<ActivityResponse, { body: UpdateActivitiesStateBody; queries?: UpdateActivitiesStateQueries }> = async ({
        body,
        queries,
    }) => {
        try {
            const response: ActivityResponse = await updateActivitiesState(body, queries);
            return response;
        } catch (error) {
            throw new Error('An unexpected error occurred during activity');
        }
    };

    return useMutation(activityMutation, options);
};

type UseImportLastNeedsMutationOptions = MutationOptions<DataItem[], Error, { siteId: string; date: string }>;

export const useImportLastNeedsStateMutation = (options?: UseImportLastNeedsMutationOptions) => {
    const activityMutation: MutationFunction<DataItem[], { siteId: string; date: string }> = async (body) => {
        try {
            const response: DataItem[] = (await importLastNeeds(body.siteId, body.date)).data;
            return response;
        } catch (error) {
            throw new Error('An unexpected error occurred during activity');
        }
    };

    return useMutation(activityMutation, options);
};

type UseImportActivitiesBySiteMutationOptions = MutationOptions<
    DataItem[],
    Error,
    { siteId: string; date: string; fromState: number; toState: number; stepMode: number }
>;

export const useImportActivitiesBySiteMutation = (options?: UseImportActivitiesBySiteMutationOptions) => {
    const importActivitiesBySiteMutation: MutationFunction<
        DataItem[],
        { siteId: string; date: string; fromState: number; toState: number; stepMode: number }
    > = async ({ siteId, date, fromState, toState, stepMode }) => {
        try {
            return (await importActivitiesBySite(siteId, date, fromState, toState, stepMode)).data;
        } catch (error) {
            throw new Error('An unexpected error occurred during import activities by site');
        }
    };

    return useMutation(importActivitiesBySiteMutation, options);
};

type UseDuplicateActivityToDateMutationOptions = MutationOptions<Activity, Error, { activityId: string; body: DuplicateActivityBody }>;

export const useDuplicateActivityToDateMutation = (options?: UseDuplicateActivityToDateMutationOptions) => {
    const duplicateActivityToDateMutation: MutationFunction<Activity, { activityId: string; body: DuplicateActivityBody }> = async ({
        activityId,
        body,
    }) => {
        try {
            return (await duplicateActivityToDate(activityId, body)).data;
        } catch (error) {
            throw new Error('An unexpected error occurred during duplicate activity');
        }
    };

    return useMutation(duplicateActivityToDateMutation, options);
};

type UseDuplicateActivitiesToDateMutationOptions = MutationOptions<Activity, Error, { body: DuplicateActivitiesBody }>;

export const useDuplicateActivitiesToDateMutation = (options?: UseDuplicateActivitiesToDateMutationOptions) => {
    const duplicateActivitiesToDateMutation: MutationFunction<Activity, { body: DuplicateActivitiesBody }> = async ({ body }) => {
        try {
            return (await duplicateActivitiesToDate(body)).data;
        } catch (error) {
            throw new Error('An unexpected error occurred during duplicate activities');
        }
    };

    return useMutation(duplicateActivitiesToDateMutation, options);
};

type UseValidateSiteActivitiesMutationOptions = MutationOptions<
    ValidateSiteActivitiesResponse,
    Error,
    { siteId: string; body: ValidateActivitiesBody }
>;

export const useValidateSiteActivitiesMutation = (options?: UseValidateSiteActivitiesMutationOptions) => {
    const mutationFn: MutationFunction<ValidateSiteActivitiesResponse, { siteId: string; body: ValidateActivitiesBody }> = async ({
        siteId,
        body,
    }) => {
        return await validateSiteActivities(siteId, body);
    };
    return useMutation(mutationFn, options);
};

type UseUpdateActivityMutationOptions = MutationOptions<ActivityResponse, Error, { id: string; body: any }>;

export const useUpdateActivityMutation = (options?: UseUpdateActivityMutationOptions) => {
    const mutationFn: MutationFunction<ActivityResponse, { id: string; body: any }> = async ({ id, body }) => {
        try {
            return await updateActivity(id, body);
        } catch (error) {
            throw new Error('An unexpected error occurred during update activity');
        }
    };

    return useMutation(mutationFn, options);
};

type UseTransferDailyElementsMutationOptions = MutationOptions<TransferDailyElementsResponse, Error, TransferDailyElementsBody>;

export const useTransferDailyElementsMutation = (options?: UseTransferDailyElementsMutationOptions) => {
    const mutationFn: MutationFunction<TransferDailyElementsResponse, TransferDailyElementsBody> = async (body) => {
        try {
            return await transferDailyElements(body);
        } catch (error) {
            throw new Error('An unexpected error occurred during daily elements transfer');
        }
    };
    return useMutation(mutationFn, options);
};

type UseDeleteActivityMutationOptions = MutationOptions<DeleteActivityResponse, Error, { id: string }>;

export const useDeleteActivityMutation = (options?: UseDeleteActivityMutationOptions) => {
    const mutationFn: MutationFunction<DeleteActivityResponse, { id: string }> = async ({ id }) => {
        try {
            return await deleteActivity(id);
        } catch (error) {
            throw new Error('An unexpected error occurred during delete activity');
        }
    };

    return useMutation(mutationFn, options);
};
